import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { ClipboardList } from 'lucide-react';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  ConfirmationDialog,
  DatePicker,
  Label,
  Textarea,
  VirtualizedFormSelect,
} from '@roger/ui-components';
import {
  getAdmissionAdmissionsAdmissionIdGet as getAdmission,
  updateAdmissionAdmissionsAdmissionIdPatch as updateAdmission,
  listInternalReferralSourcesForIntakeInternalReferralSourcesGet as listInternalReferralSources,
} from '../../services/api/sdk.gen';
import type {
  AdmissionResponse,
  AdmissionSourceCode,
  AdmissionTypeWorksheetImpactResponse,
  AdmissionUpdate,
  InternalReferralSourceResponse,
} from '../../services/api/types.gen';
import { useToast } from '../../hooks/useToastContext';
import { CHART_AUTOSAVE_DELAY_MS, useAutoSave } from '../../hooks/useAutoSave';
import { AutoSavePill } from '../AutoSavePill';
import {
  getBusinessErrorMessage,
  getResponseDetail,
} from '../../utils/apiErrorDetail';
import { getHttpStatus } from '../../utils/httpErrorUtils';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../../utils/dateUtils';
import { ADMISSION_SOURCE_OPTIONS } from '../../hooks/useCreatePatientForm';
import { UB04_ADMISSION_TYPE_OPTIONS } from '../../constants/billing';
import { PermissionSlug } from '../../constants/featureFlags';
import { useStore } from '../../store';
import { EntityWorkLogDialog } from '../worklog/EntityWorkLogDialog';
import {
  ADMISSION_WORK_LOG_DIALOG_PROPS,
  loadAdmissionWorkLog,
} from '../worklog/admissionWorkLog';

interface AdmissionTabProps {
  /** The admission that parents the currently selected episode. */
  admissionId: string | null | undefined;
  /** Clinic the patient belongs to, which scopes the referral source list. */
  clinicId: string | null | undefined;
}

// admission.admission_type is stored as UB-04 FL14, so it is labelled from the
// FL14 set. Kinnser's own admission-type select agrees on 1 to 5 and then ends
// at 6 where FL14 uses 9, so labelling from that list instead would print
// nothing at all for an "information not available" admission.
const ADMISSION_TYPE_LABELS: Record<string, string> = Object.fromEntries(
  UB04_ADMISSION_TYPE_OPTIONS.map(option => [
    option.value,
    option.label
      .replace(/^\d+\s+—\s+/, '')
      .replace('Trauma center', 'Trauma')
      .replace('Information not available', 'Information not Available'),
  ])
);

const ADMISSION_TYPE_OPTIONS = UB04_ADMISSION_TYPE_OPTIONS.map(option => ({
  value: option.value,
  label: ADMISSION_TYPE_LABELS[option.value] ?? option.label,
}));

interface SourceOption {
  id: string;
  name: string;
}

interface AdmissionTypeWorksheetImpact {
  value: number;
  worksheetCount: number;
  noaSent: boolean;
}

function isAdmissionTypeWorksheetImpactDetail(
  detail: unknown
): detail is AdmissionTypeWorksheetImpactResponse['detail'] {
  if (!detail || typeof detail !== 'object') return false;
  const candidate = detail as Record<string, unknown>;
  return (
    candidate.code === 'admission_type_worksheet_impact' &&
    typeof candidate.message === 'string' &&
    typeof candidate.worksheet_count === 'number' &&
    Number.isInteger(candidate.worksheet_count) &&
    candidate.worksheet_count >= 0
  );
}

function parseAdmissionTypeWorksheetImpact(
  error: unknown
): { worksheetCount: number; noaSent: boolean } | null {
  if (getHttpStatus(error) !== 409) return null;
  const detail = getResponseDetail(error);
  return isAdmissionTypeWorksheetImpactDetail(detail)
    ? {
        worksheetCount: detail.worksheet_count,
        noaSent: detail.noa_sent === true,
      }
    : null;
}

/** The subset of the admission this tab lets staff correct after the fact. */
interface AdmissionFormState {
  referralSourceId: string;
  referralDate: Date | undefined;
  faceToFaceDate: Date | undefined;
  notes: string;
  admissionSourceCode: string;
}

function admissionToForm(admission: AdmissionResponse): AdmissionFormState {
  return {
    referralSourceId: admission.internal_referral_source_id ?? '',
    referralDate: admission.referral_received_date
      ? (parseISOToLocalDate(admission.referral_received_date) ?? undefined)
      : undefined,
    faceToFaceDate: admission.face_to_face_date
      ? (parseISOToLocalDate(admission.face_to_face_date) ?? undefined)
      : undefined,
    notes: admission.notes ?? '',
    // Held as the stored string, not narrowed to the codes the dropdown lists.
    // Rows written before this screen existed carry codes the UB-04 enum does
    // not name, and dropping one here would both hide it from the user and put
    // the baseline out of step with what the server holds.
    admissionSourceCode: admission.admission_source_code ?? '',
  };
}

/**
 * The form as the API stores it.
 *
 * This is the one place form values are normalised, and both the request body
 * and the dirty check are derived from it. Keeping them on a single function
 * means a new editable field cannot be added to the write while the comparison
 * still reads the form as clean, and the trim semantics cannot drift apart so
 * that a saved value gets adopted back over what is on screen.
 */
type AdmissionStoredShape = {
  internal_referral_source_id: AdmissionUpdate['internal_referral_source_id'];
  referral_received_date: AdmissionUpdate['referral_received_date'];
  face_to_face_date: AdmissionUpdate['face_to_face_date'];
  notes: AdmissionUpdate['notes'];
  admission_source_code: AdmissionUpdate['admission_source_code'];
};

function toStoredShape(form: AdmissionFormState): AdmissionStoredShape {
  return {
    internal_referral_source_id: form.referralSourceId || null,
    referral_received_date: form.referralDate
      ? formatDateToISO(form.referralDate)
      : null,
    face_to_face_date: form.faceToFaceDate
      ? formatDateToISO(form.faceToFaceDate)
      : null,
    notes: form.notes.trim() || null,
    // The write schema names the valid UB-04 codes while the read schema does
    // not, because a legacy code has to survive being displayed. Form state
    // therefore holds the wider type and narrows here, at the one boundary
    // where a value becomes a request. Sending a legacy code back is refused
    // by the server, and only a deliberate edit ever sends this field at all.
    admission_source_code: (form.admissionSourceCode ||
      null) as AdmissionSourceCode | null,
  };
}

function admissionFormsEqual(
  a: AdmissionFormState,
  b: AdmissionFormState
): boolean {
  const left = toStoredShape(a);
  const right = toStoredShape(b);
  return (
    left.internal_referral_source_id === right.internal_referral_source_id &&
    left.referral_received_date === right.referral_received_date &&
    left.face_to_face_date === right.face_to_face_date &&
    left.notes === right.notes &&
    left.admission_source_code === right.admission_source_code
  );
}

/** The fields whose stored value differs, so a save writes nothing else. */
function storedShapeDiff(
  next: AdmissionStoredShape,
  previous: AdmissionStoredShape
): AdmissionUpdate {
  const body: AdmissionUpdate = {};
  if (
    next.internal_referral_source_id !== previous.internal_referral_source_id
  ) {
    body.internal_referral_source_id = next.internal_referral_source_id;
  }
  if (next.referral_received_date !== previous.referral_received_date) {
    body.referral_received_date = next.referral_received_date;
  }
  if (next.face_to_face_date !== previous.face_to_face_date) {
    body.face_to_face_date = next.face_to_face_date;
  }
  if (next.notes !== previous.notes) {
    body.notes = next.notes;
  }
  if (next.admission_source_code !== previous.admission_source_code) {
    body.admission_source_code = next.admission_source_code;
  }
  return body;
}

function ReadOnlyField({
  label,
  value,
  hint,
}: {
  label: string;
  value: string;
  hint?: string;
}) {
  return (
    <div className='grid gap-1'>
      <Label className='text-muted-foreground'>{label}</Label>
      <p className='text-sm font-medium'>{value}</p>
      {hint && <p className='text-xs text-muted-foreground'>{hint}</p>}
    </div>
  );
}

/**
 * Saves a card started as it unmounted, keyed by the admission they target.
 *
 * This lives at module scope rather than in `AdmissionTab` state because the
 * chart renders the tab conditionally: clicking another chart tab unmounts
 * `AdmissionTab` itself, and that is one of the exits a flush is started on.
 * A registry that went with it would be discarded exactly when it is needed.
 *
 * Reading an admission back has to wait for its own outstanding write first:
 * otherwise the fetch can read the record mid-write and seed pre-save values
 * as the new baseline, and the edit disappears from the screen.
 */
const pendingAdmissionFlushes = new Map<string, Promise<unknown>>();
let registryBeforeUnloadAttached = false;

const registryBeforeUnloadHandler = (event: BeforeUnloadEvent) => {
  event.preventDefault();
  event.returnValue = '';
};

function syncRegistryBeforeUnloadListener() {
  if (typeof window === 'undefined') return;
  const shouldAttach = pendingAdmissionFlushes.size > 0;
  if (shouldAttach && !registryBeforeUnloadAttached) {
    window.addEventListener('beforeunload', registryBeforeUnloadHandler);
    registryBeforeUnloadAttached = true;
  } else if (!shouldAttach && registryBeforeUnloadAttached) {
    window.removeEventListener('beforeunload', registryBeforeUnloadHandler);
    registryBeforeUnloadAttached = false;
  }
}

function registerAdmissionFlush(admissionId: string, flush: Promise<unknown>) {
  // Keep every write for this admission in the registry. The caller keeps the
  // original promise so it can handle its own failure; only this registry
  // converts a failure into a settled wait for the next chart load.
  const previous = pendingAdmissionFlushes.get(admissionId);
  const combined = previous
    ? previous.then(
        () => flush,
        () => flush
      )
    : flush;
  const tracked = combined.catch(() => undefined);
  pendingAdmissionFlushes.set(admissionId, tracked);
  syncRegistryBeforeUnloadListener();
  void tracked.then(() => {
    if (pendingAdmissionFlushes.get(admissionId) === tracked) {
      pendingAdmissionFlushes.delete(admissionId);
      syncRegistryBeforeUnloadListener();
    }
  });
}

interface AdmissionCardProps {
  admission: AdmissionResponse;
  sourceOptions: SourceOption[];
  onSaved: (updated: AdmissionResponse) => void;
}

/**
 * One loaded admission, with its editable fields on autosave.
 *
 * Mounted only while an admission is on screen and keyed on that admission's
 * id, so switching episodes gives the form a fresh baseline and an empty
 * debounce rather than carrying the previous admission's edits into the next
 * one. That remount, not a guard inside the save, is what makes it impossible
 * to write one episode's edits onto another episode's admission.
 */
const AdmissionCard: React.FC<AdmissionCardProps> = ({
  admission,
  sourceOptions,
  onSaved,
}) => {
  const { error: showError } = useToast();

  // The log needs both: worklog:view alone covers roles that must not read
  // every record's history, and the server refuses either one without the
  // other.
  const hasPermission = useStore(state => state.hasPermission);
  const canViewWorkLog =
    hasPermission(PermissionSlug.WORKLOG_VIEW) &&
    hasPermission(PermissionSlug.PATIENT_INFO_EDIT);
  const [workLogOpen, setWorkLogOpen] = useState(false);

  // Whether this form is still on screen. The unmount flush below completes
  // its request after the component is gone, and handing that response back to
  // the parent would re-seat the admission the user has navigated away from.
  const isMountedRef = useRef(true);
  useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
    };
  }, []);

  const admissionId = admission.id;

  // This card is keyed on the admission, so the log it opens is always the
  // log of the admission behind the episode selected in the chart header.
  const loadWorkLog = useCallback(
    (cursor?: string) => loadAdmissionWorkLog(admissionId, cursor),
    [admissionId]
  );
  const resolvedSocDate = admission.resolved_soc_date;

  // Stable across the parent's own re-renders, such as the referral source
  // list arriving, so the hook only reconsiders an external value when the
  // record itself is replaced.
  const initialForm = useMemo(() => admissionToForm(admission), [admission]);

  // A stored code the UB-04 list does not name stays selectable, the same way
  // a retired referral source does: the chart shows it today, and an option
  // list without it would render the field empty for a patient who does have a
  // source on file. The server already resolves the display text, falling back
  // to the bare code when it cannot name it, so the label comes from there
  // rather than from a second guess here. Choosing any real code replaces it,
  // which is the point of the field.
  //
  // Carried in the same `{id, name}` shape as the referral source list beside
  // it. The select takes either shape, but one shape per screen means the two
  // dropdowns read the same and neither needs its own mental model.
  const storedSourceCode = admission.admission_source_code;
  const storedSourceLabel = admission.admission_source_label;
  const admissionSourceOptions = useMemo<SourceOption[]>(() => {
    const options = ADMISSION_SOURCE_OPTIONS.map(option => ({
      id: option.value,
      name: option.label,
    }));
    if (
      storedSourceCode &&
      !options.some(option => option.id === storedSourceCode)
    ) {
      options.push({
        id: storedSourceCode,
        name: storedSourceLabel ?? storedSourceCode,
      });
    }
    return options;
  }, [storedSourceCode, storedSourceLabel]);
  const admissionTypeOptions = useMemo<SourceOption[]>(() => {
    const options = ADMISSION_TYPE_OPTIONS.map(option => ({
      id: option.value,
      name: option.label,
    }));
    if (
      admission.admission_type !== null &&
      admission.admission_type !== undefined &&
      !options.some(option => option.id === String(admission.admission_type))
    ) {
      options.push({
        id: String(admission.admission_type),
        name: String(admission.admission_type),
      });
    }
    return options;
  }, [admission.admission_type]);

  const persistedAdmissionType =
    admission.admission_type === null || admission.admission_type === undefined
      ? ''
      : String(admission.admission_type);
  const persistedAdmissionTypeRef = useRef(persistedAdmissionType);
  const [displayedAdmissionType, setDisplayedAdmissionType] = useState(
    persistedAdmissionType
  );

  // The shared select keeps its own selected option after user interaction, so
  // it needs an explicit local value to show the server value again after a
  // rejected write.
  useEffect(() => {
    persistedAdmissionTypeRef.current = persistedAdmissionType;
    setDisplayedAdmissionType(persistedAdmissionType);
  }, [persistedAdmissionType]);

  // What the server currently holds, so each save can write only what changed.
  const persistedRef = useRef<AdmissionStoredShape>(toStoredShape(initialForm));
  // The stored shape whose save already failed and was already reported. The
  // unmount flush retries the same value, and a second identical toast on a
  // screen the user has already moved to says nothing the first did not.
  const reportedFailureRef = useRef<string | null>(null);

  // Both kinds of Admission PATCH return the whole record. Keeping their
  // requests, baseline updates, and parent callbacks in one queue means a
  // later full response cannot be applied before an earlier one. The queue
  // tail always settles, including after a rejected 409, so the acknowledgement
  // retry can enqueue normally.
  const requestQueueRef = useRef(Promise.resolve());
  const enqueueRequest = useCallback(<T,>(request: () => Promise<T>) => {
    const queued = requestQueueRef.current.then(request, request);
    requestQueueRef.current = queued.then(
      () => undefined,
      () => undefined
    );
    return queued;
  }, []);

  const handleSave = useCallback(
    async (form: AdmissionFormState) => {
      const next = toStoredShape(form);
      try {
        const result = await enqueueRequest(async () => {
          // The generic form may have been queued behind an explicit type
          // request. Recompute its narrow body at execution time rather than
          // replaying a diff made against an older baseline.
          const body = storedShapeDiff(next, persistedRef.current);
          if (Object.keys(body).length === 0) return null;
          const response = await updateAdmission({
            path: { admission_id: admissionId },
            body,
            throwOnError: true,
          });
          // Only fields included in this request advance the generic
          // baseline; an older snapshot must not overwrite newer fields.
          persistedRef.current = { ...persistedRef.current, ...body };
          // Apply the full response before releasing the queue to the next
          // request, otherwise its callback could arrive out of order.
          if (isMountedRef.current && response.data) {
            onSaved(response.data);
          }
          return response;
        });
        if (!result) return;
        reportedFailureRef.current = null;
      } catch (e) {
        console.error('failed to update admission', e);
        const failureKey = JSON.stringify(next);
        if (isMountedRef.current && reportedFailureRef.current !== failureKey) {
          reportedFailureRef.current = failureKey;
          showError(
            getBusinessErrorMessage(e) ??
              'Unable to save admission. Please try again.'
          );
        }
        // Rethrow so the pill reports the failure instead of claiming a save
        // that did not happen.
        throw e;
      }
    },
    [admissionId, enqueueRequest, onSaved, showError]
  );

  const {
    value: form,
    setValue: setForm,
    saveStatus,
    saveNow,
    hasUnsavedChanges,
    changeToken,
  } = useAutoSave<AdmissionFormState>({
    initialValue: initialForm,
    onSave: handleSave,
    debounceDelay: CHART_AUTOSAVE_DELAY_MS,
    // Latch a failure until the next edit. Fading it would leave the tab
    // looking clean while the edit is still unpersisted, and nothing retries
    // on its own.
    errorStatusDuration: null,
    isEqual: admissionFormsEqual,
  });

  const [admissionTypeSaving, setAdmissionTypeSaving] = useState(false);
  const [pendingAdmissionType, setPendingAdmissionType] =
    useState<AdmissionTypeWorksheetImpact | null>(null);

  const saveAdmissionType = useCallback(
    async (value: number, acknowledgeWorksheetUpdates = false) => {
      setAdmissionTypeSaving(true);
      try {
        const queuedRequest = enqueueRequest(async () => {
          const response = await updateAdmission({
            path: { admission_id: admissionId },
            body: { admission_type: value },
            query: acknowledgeWorksheetUpdates
              ? { acknowledge_worksheet_updates: true }
              : undefined,
            throwOnError: true,
          });
          // The response is the authority. The local value only mirrors the
          // select while this request is pending; adopt a new type after the
          // server confirms it and before releasing the request queue.
          if (isMountedRef.current && response.data) {
            const savedAdmissionType = response.data.admission_type;
            persistedAdmissionTypeRef.current =
              savedAdmissionType === null || savedAdmissionType === undefined
                ? ''
                : String(savedAdmissionType);
            setDisplayedAdmissionType(persistedAdmissionTypeRef.current);
            onSaved(response.data);
          }
          return response;
        });
        registerAdmissionFlush(admissionId, queuedRequest);
        const result = await queuedRequest;
        if (isMountedRef.current) setPendingAdmissionType(null);
        return result;
      } catch (error) {
        if (!isMountedRef.current) return;
        // A failed request leaves the server value authoritative. Reset the
        // transient selection before showing either the worksheet dialog or
        // the error toast.
        setDisplayedAdmissionType(persistedAdmissionTypeRef.current);
        const impact = parseAdmissionTypeWorksheetImpact(error);
        if (!acknowledgeWorksheetUpdates && impact !== null) {
          setPendingAdmissionType({ value, ...impact });
          return;
        }
        console.error('failed to update admission type', error);
        showError(
          getHttpStatus(error) === 409
            ? 'Unable to save admission type. Please try again.'
            : (getBusinessErrorMessage(error) ??
                'Unable to save admission type. Please try again.')
        );
      } finally {
        if (isMountedRef.current) setAdmissionTypeSaving(false);
      }
    },
    [admissionId, enqueueRequest, onSaved, showError]
  );

  const admissionTypeImpactMessage = pendingAdmissionType
    ? [
        pendingAdmissionType.noaSent
          ? 'The notice of admission (NOA) was already sent with the current admission type. It will not be resent, and its 837I and UB-04 exports will show the new type.'
          : null,
        pendingAdmissionType.worksheetCount > 0
          ? `This will update ${pendingAdmissionType.worksheetCount} open billing worksheet${pendingAdmissionType.worksheetCount === 1 ? '' : 's'}.`
          : null,
        'Do you want to continue?',
      ]
        .filter(Boolean)
        .join(' ')
    : '';

  // Mirrored in an effect rather than during render: a render React discards
  // must not leave the ref holding a save bound to values that never
  // committed.
  const saveNowRef = useRef(saveNow);
  useEffect(() => {
    saveNowRef.current = saveNow;
  }, [saveNow]);

  // Leaving the tab or switching episodes unmounts this form, which would
  // otherwise drop an edit still sitting in the debounce window. The request
  // is addressed to the admission captured above, so a flush that lands late
  // still writes to the record the edit was made on, and it is registered
  // outside this tree so the next read of that admission waits for it even
  // when the whole tab went away.
  useEffect(
    () => () => {
      registerAdmissionFlush(admissionId, saveNowRef.current());
    },
    [admissionId]
  );

  // Autosave took away the Save button, so a reload or a closed tab is the one
  // exit React cleanup cannot cover. Warn while anything is still outstanding,
  // matching the reload guard OASISForm uses for a pending carryover.
  //
  // A failed save is covered by the dirty check rather than by the status: the
  // hook leaves its baseline untouched on failure, so anything worth warning
  // about is still unsaved. The latched 'error' outlives a revert back to the
  // persisted text, and prompting then would guard nothing.
  const isOutstanding =
    hasUnsavedChanges || saveStatus === 'saving' || admissionTypeSaving;
  useEffect(() => {
    if (!isOutstanding) return;
    const handler = (event: BeforeUnloadEvent) => {
      event.preventDefault();
      event.returnValue = '';
    };
    window.addEventListener('beforeunload', handler);
    return () => window.removeEventListener('beforeunload', handler);
  }, [isOutstanding]);

  // The hook adopts a newly loaded record as its baseline only while this form
  // is clean, and adoption is what makes the two equal. Advancing the diff
  // basis on exactly that condition keeps it level with the hook rather than
  // stuck on an older record — while a form still showing the old value for a
  // field someone else changed keeps its narrower basis, so its next save
  // does not carry that stale field along.
  useEffect(() => {
    if (admissionFormsEqual(form, initialForm)) {
      persistedRef.current = toStoredShape(initialForm);
    }
  }, [form, initialForm]);

  const updateField = <K extends keyof AdmissionFormState>(
    key: K,
    value: AdmissionFormState[K]
  ) => {
    setForm({ ...form, [key]: value });
  };

  return (
    <>
      <Card>
        <CardHeader>
          <div className='flex items-center justify-between gap-2'>
            <div className='flex items-center gap-2'>
              <ClipboardList className='h-4 w-4 text-muted-foreground' />
              <h3 className='text-base font-semibold'>Admission</h3>
            </div>
            <div className='flex items-center gap-2'>
              <AutoSavePill
                status={saveStatus}
                debounceMs={CHART_AUTOSAVE_DELAY_MS}
                changeToken={changeToken}
              />
              {canViewWorkLog && (
                <Button
                  onClick={() => setWorkLogOpen(true)}
                  size='sm'
                  variant='outline'
                >
                  <ClipboardList className='h-4 w-4 mr-1' />
                  Work log
                </Button>
              )}
            </div>
          </div>
        </CardHeader>
        <CardContent className='space-y-6'>
          <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
            <ReadOnlyField
              label='Start of Care'
              value={formatDateToMMDDYYYY(admission.resolved_soc_date)}
            />
            <ReadOnlyField label='Status' value={admission.status} />
            <ReadOnlyField
              label='Discharge Date'
              value={
                admission.discharge_date
                  ? formatDateToMMDDYYYY(admission.discharge_date)
                  : 'Not discharged'
              }
            />
            <div className='grid gap-1'>
              <VirtualizedFormSelect
                label='Admission Type'
                placeholder='Select admission type...'
                options={admissionTypeOptions}
                value={displayedAdmissionType}
                disabled={admissionTypeSaving}
                onChange={e => {
                  const value = Number(e.target.value);
                  if (Number.isInteger(value)) {
                    // This is only a transient visual mirror of the user's
                    // selection. The response below decides what remains.
                    setDisplayedAdmissionType(e.target.value);
                    void saveAdmissionType(value);
                  }
                }}
              />
              <p className='text-xs text-muted-foreground'>Used on claims.</p>
            </div>
            {/* The hint sits alongside the select rather than on it: the shared
              select takes no hint prop, and this field needs one more than the
              read-only fields beside it do. Point of origin reaches a claim
              through a worksheet snapshot, so a biller has to refresh before
              the change shows up there. Saying so here is cheaper than
              learning it from a claim. */}
            <div className='grid gap-1'>
              <VirtualizedFormSelect
                label='Admission Source'
                placeholder='Not set'
                options={admissionSourceOptions}
                value={form.admissionSourceCode}
                onChange={e =>
                  updateField('admissionSourceCode', e.target.value)
                }
              />
              <p className='text-xs text-muted-foreground'>
                Used on claims. A biller refreshes the worksheet to pick this
                up.
              </p>
            </div>
          </div>

          <div className='grid grid-cols-1 gap-4 md:grid-cols-3'>
            <VirtualizedFormSelect
              label='Internal Referral Source'
              placeholder='Select referral source...'
              options={sourceOptions}
              sortOptions
              value={form.referralSourceId}
              onChange={e => updateField('referralSourceId', e.target.value)}
            />

            <div className='grid gap-2'>
              <Label>Referral Date</Label>
              <DatePicker
                date={form.referralDate}
                onDateChange={date => updateField('referralDate', date)}
                datePickerSize='default'
                showClearButton
              />
            </div>

            <div className='grid gap-2'>
              <Label>Face-to-Face Date</Label>
              <DatePicker
                date={form.faceToFaceDate}
                onDateChange={date => updateField('faceToFaceDate', date)}
                datePickerSize='default'
                showClearButton
              />
            </div>
          </div>

          <div className='grid gap-2'>
            <Label>Notes</Label>
            <Textarea
              textareaSize='default'
              value={form.notes}
              onChange={e => updateField('notes', e.target.value)}
              rows={4}
            />
          </div>
        </CardContent>
      </Card>
      <ConfirmationDialog
        open={pendingAdmissionType !== null}
        title={
          pendingAdmissionType?.noaSent
            ? 'Change admission type after the NOA was sent?'
            : 'Update billing worksheets?'
        }
        message={admissionTypeImpactMessage}
        confirmLabel='Continue'
        cancelLabel='Cancel'
        loading={admissionTypeSaving}
        onAccept={() => {
          if (pendingAdmissionType) {
            void saveAdmissionType(pendingAdmissionType.value, true);
          }
        }}
        onCancel={() => {
          if (isMountedRef.current) {
            setDisplayedAdmissionType(persistedAdmissionTypeRef.current);
            setPendingAdmissionType(null);
          }
        }}
      />
      {canViewWorkLog && (
        <EntityWorkLogDialog
          open={workLogOpen}
          onOpenChange={setWorkLogOpen}
          {...ADMISSION_WORK_LOG_DIALOG_PROPS}
          description={
            <>
              Changes to this admission, oldest first.
              {resolvedSocDate ? (
                <span className='block text-foreground'>
                  Admission: SOC {formatDateToMMDDYYYY(resolvedSocDate)}
                </span>
              ) : null}
            </>
          }
          load={loadWorkLog}
        />
      )}
    </>
  );
};

/**
 * Admission details for the episode selected in the chart header.
 *
 * One patient can have several admissions, so this always shows the one that
 * parents the selected episode rather than the patient's newest.
 *
 * Only the fields staff can safely correct after the fact are editable. Start of
 * care, status and discharge date drive episode lifecycle, so they remain read-only.
 *
 * Admission Source and Admission Type are editable claim-bearing fields.
 * Admission Source marks refreshable worksheets stale; Admission Type updates
 * eligible open worksheet snapshots after confirmation.
 */
const AdmissionTab: React.FC<AdmissionTabProps> = ({
  admissionId,
  clinicId,
}) => {
  const { error: showError } = useToast();

  const [admission, setAdmission] = useState<AdmissionResponse | null>(null);
  const [sources, setSources] = useState<InternalReferralSourceResponse[]>([]);
  // Whether the active-source list actually arrived. Absence from a list that
  // failed to load says nothing about a source, so this gates the "(retired)"
  // label below.
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    // Drop the outgoing admission before the new one arrives. This clear is
    // what unmounts the form, and the form is the only thing that can save, so
    // it is also what stops a pending autosave from writing one episode's
    // edits onto another episode's admission. Keep it even though the load
    // below overwrites the same state.
    setAdmission(null);
    if (!admissionId) {
      return;
    }
    let cancelled = false;
    const load = async () => {
      setIsLoading(true);
      try {
        const pending = pendingAdmissionFlushes.get(admissionId);
        if (pending) {
          await pending;
          if (cancelled) return;
        }
        const result = await getAdmission({
          path: { admission_id: admissionId },
          throwOnError: true,
        });
        if (!cancelled && result.data) {
          setAdmission(result.data);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('failed to load admission', e);
          showError('Unable to load admission. Please try again.');
        }
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [admissionId, showError]);

  useEffect(() => {
    if (!clinicId) {
      setSources([]);
      setSourcesLoaded(false);
      return;
    }
    let cancelled = false;
    const load = async () => {
      // The list in hand belongs to the previous clinic. Nothing in it can
      // justify calling a source retired, and nothing in it belongs in this
      // clinic's dropdown, so drop it rather than leaving the invariant to
      // whether the parent happens to remount on a clinic change.
      setSources([]);
      setSourcesLoaded(false);
      try {
        const result = await listInternalReferralSources({
          query: { clinic_id: clinicId },
          throwOnError: true,
        });
        if (!cancelled && result.data) {
          setSources(result.data);
          setSourcesLoaded(true);
        }
      } catch (e) {
        if (!cancelled) {
          console.error('failed to load internal referral sources', e);
          setSources([]);
          setSourcesLoaded(false);
        }
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [clinicId]);

  // Only the attribution actually drives the list, so a save that changes
  // nothing else does not hand the card a new array identity.
  const currentSourceId = admission?.internal_referral_source_id;
  const currentSourceName = admission?.internal_referral_source_name;

  // A retired source stays selectable on an admission already using it, so the
  // dropdown does not silently blank out an existing attribution on save. The
  // select is left to order them.
  const sourceOptions = useMemo(() => {
    const options = sources.map(source => ({
      id: source.id,
      name: `${source.first_name} ${source.last_name}`.trim(),
    }));
    if (currentSourceId && !options.some(o => o.id === currentSourceId)) {
      // Only call it retired when the active list actually loaded. If that
      // request failed or is still in flight, everything is missing from the
      // list, and labelling a live marketer "(retired)" would state something
      // false about the record rather than admit the list is unavailable.
      const name = currentSourceName ?? 'Unknown';
      options.push({
        id: currentSourceId,
        name: sourcesLoaded ? `${name} (retired)` : name,
      });
    }
    return options;
  }, [sources, sourcesLoaded, currentSourceId, currentSourceName]);

  if (!admissionId) {
    return (
      <div className='rounded-md border p-6 text-center text-sm text-muted-foreground'>
        Select an episode to view its admission.
      </div>
    );
  }

  if (isLoading && !admission) {
    return (
      <div className='rounded-md border p-6 text-center text-sm text-muted-foreground'>
        Loading admission...
      </div>
    );
  }

  if (!admission) {
    return (
      <div className='rounded-md border p-6 text-center text-sm text-muted-foreground'>
        Admission not found.
      </div>
    );
  }

  return (
    <AdmissionCard
      key={admission.id}
      admission={admission}
      sourceOptions={sourceOptions}
      onSaved={setAdmission}
    />
  );
};

export default AdmissionTab;
